/**
 * natureLiveConfig — the live-tunable store for the Tree & Nature system
 * (lift-ready checklist §2: "config is pure declarative data … resolves to a
 * flat spec object"). Exposed as a live, mutable object so DJ can tune the
 * vegetation feel from a GUI without a reload — the NatureLayer reads these by
 * reference at mount time, and a re-mount applies the new values.
 *
 * Mirrors the mobBumpConfig / interpolationConfig live-store pattern. The three
 * multipliers are the minimal live surface: density (how many plants), scale
 * (how big), alpha (how visible). Per-biome tuning lives in natureConfig.ts
 * (declarative data, not live-tunable); these multipliers compose on top.
 */
export interface NatureLiveConfig {
  /** Density multiplier (1 = authored density; 0 = no vegetation; 2 = double). */
  densityMultiplier: number;
  /** Scale multiplier on plant height/width (1 = authored sizes). */
  scaleMultiplier: number;
  /** Alpha multiplier on plant opacity (1 = authored alpha). */
  alphaMultiplier: number;
}

export const NATURE_LIVE_DEFAULT: Readonly<NatureLiveConfig> = {
  densityMultiplier: 1,
  scaleMultiplier: 1,
  alphaMultiplier: 1,
};

/** Live, mutable store read by NatureLayer at mount + written by the GUI. */
export const natureLiveConfig: NatureLiveConfig = { ...NATURE_LIVE_DEFAULT };

export function setNatureLive(partial: Partial<NatureLiveConfig>): void {
  Object.assign(natureLiveConfig, partial);
}

export function resetNatureLive(): void {
  Object.assign(natureLiveConfig, NATURE_LIVE_DEFAULT);
}

if (typeof window !== 'undefined') {
  (window as any).__NATURE_LIVE_CONFIG__ = natureLiveConfig;
}
